import { z } from "zod/v3";
export const pointSchema = z.object({
  x: z.number().finite().min(-20000).max(20000),
  y: z.number().finite().min(-20000).max(20000),
});
export const vectorNodeSchema = pointSchema.extend({
  in: pointSchema.optional(),
  out: pointSchema.optional(),
});
export const vectorSchema = z
  .object({
    width: z.number().finite().min(1).max(10000),
    height: z.number().finite().min(1).max(10000),
    nodes: z.array(vectorNodeSchema).min(2).max(256),
    closed: z.boolean().default(false),
  })
  .refine(
    (v) => !v.closed || v.nodes.length >= 3,
    "Closed paths need at least three points.",
  );
export type Point = z.infer<typeof pointSchema>;
export type VectorNode = z.infer<typeof vectorNodeSchema>;
export type Vector = z.infer<typeof vectorSchema>;
export type VectorPreset = "line" | "triangle" | "polygon" | "star";
export const vectorPresetSchema = z.enum([
  "line",
  "triangle",
  "polygon",
  "star",
]);
export const strokeCapSchema = z.enum(["butt", "round", "square"]);
export const strokeJoinSchema = z.enum(["miter", "round", "bevel"]);

const round = (n: number) => Math.round(n * 10000) / 10000;
/** SVG geometry is scaled into design pixels; resizing keeps stroke width unchanged. */
export function vectorPath(
  vector: Vector,
  width = vector.width,
  height = vector.height,
) {
  const point = (p: Point) =>
    round((p.x * width) / vector.width) +
    " " +
    round((p.y * height) / vector.height);
  const nodes = vector.nodes;
  if (!nodes.length) return "";
  let d = "M " + point(nodes[0]);
  const segment = (a: VectorNode, b: VectorNode) =>
    a.out || b.in
      ? " C " + point(a.out || a) + " " + point(b.in || b) + " " + point(b)
      : " L " + point(b);
  for (let i = 1; i < nodes.length; i++) d += segment(nodes[i - 1], nodes[i]);
  if (vector.closed) d += segment(nodes[nodes.length - 1], nodes[0]) + " Z";
  return d;
}
export function vectorPreset(preset: VectorPreset, sides = 5): Vector {
  if (preset === "line")
    return {
      width: 220,
      height: 1,
      nodes: [
        { x: 0, y: 0 },
        { x: 220, y: 0 },
      ],
      closed: false,
    };
  const count =
    preset === "triangle" ? 3 : Math.max(3, Math.min(32, Math.round(sides)));
  const length = preset === "star" ? count * 2 : count;
  return {
    width: 200,
    height: 200,
    closed: true,
    nodes: Array.from({ length }, (_, i) => {
      const angle = -Math.PI / 2 + (i * Math.PI * 2) / length;
      const radius = preset === "star" && i % 2 ? 43 : 96;
      return {
        x: round(100 + Math.cos(angle) * radius),
        y: round(100 + Math.sin(angle) * radius),
      };
    }),
  };
}
export function defaultVector(): Vector {
  return {
    width: 220,
    height: 160,
    closed: false,
    nodes: [
      { x: 0, y: 140, out: { x: 70, y: 140 } },
      { x: 110, y: 20, in: { x: 40, y: 20 }, out: { x: 180, y: 20 } },
      { x: 220, y: 140, in: { x: 150, y: 140 } },
    ],
  };
}
export function moveNode(node: VectorNode, point: Point): VectorNode {
  const dx = point.x - node.x,
    dy = point.y - node.y;
  return {
    ...node,
    ...point,
    ...(node.in ? { in: { x: node.in.x + dx, y: node.in.y + dy } } : {}),
    ...(node.out ? { out: { x: node.out.x + dx, y: node.out.y + dy } } : {}),
  };
}
export function smoothNode(vector: Vector, index: number): VectorNode {
  const node = vector.nodes[index];
  const before =
    vector.nodes[index - 1] || (vector.closed ? vector.nodes.at(-1)! : node);
  const after =
    vector.nodes[index + 1] || (vector.closed ? vector.nodes[0] : node);
  const dx = (after.x - before.x) / 6,
    dy = (after.y - before.y) / 6;
  return {
    ...node,
    in: { x: node.x - dx, y: node.y - dy },
    out: { x: node.x + dx, y: node.y + dy },
  };
}
const midpoint = (a: Point, b: Point): Point => ({
  x: (a.x + b.x) / 2,
  y: (a.y + b.y) / 2,
});
/** Split the outgoing segment at t=0.5 without changing its Bézier curve. */
export function splitSegment(vector: Vector, index: number): Vector {
  if (vector.nodes.length >= 256)
    throw new Error("A path supports up to 256 points.");
  if (
    index < 0 ||
    index >= vector.nodes.length ||
    (!vector.closed && index === vector.nodes.length - 1)
  )
    throw new Error("Choose a point with a following segment.");
  const nodes = structuredClone(vector.nodes),
    next = (index + 1) % nodes.length;
  const a = nodes[index],
    b = nodes[next];
  let node: VectorNode;
  if (a.out || b.in) {
    const p = midpoint(a, a.out || a),
      q = midpoint(a.out || a, b.in || b),
      r = midpoint(b.in || b, b);
    const s = midpoint(p, q),
      t = midpoint(q, r);
    node = { ...midpoint(s, t), in: s, out: t };
    nodes[index] = { ...a, out: p };
    nodes[next] = { ...b, in: r };
  } else node = midpoint(a, b);
  nodes.splice(index + 1, 0, node);
  return { ...vector, nodes };
}
export function removeNode(vector: Vector, index: number): Vector {
  if (index < 0 || index >= vector.nodes.length)
    throw new Error("Point does not exist.");
  if (vector.nodes.length <= (vector.closed ? 3 : 2))
    throw new Error(
      vector.closed
        ? "A closed path needs at least three points."
        : "An open path needs at least two points.",
    );
  return { ...vector, nodes: vector.nodes.filter((_, i) => i !== index) };
}
export function fitVector(nodes: VectorNode[], closed: boolean) {
  const points = nodes.flatMap((node) => [
    node,
    ...(node.in ? [node.in] : []),
    ...(node.out ? [node.out] : []),
  ]);
  const x = Math.min(...points.map((p) => p.x)),
    y = Math.min(...points.map((p) => p.y));
  const width = Math.max(1, Math.max(...points.map((p) => p.x)) - x),
    height = Math.max(1, Math.max(...points.map((p) => p.y)) - y);
  const shift = (p: Point) => ({ x: p.x - x, y: p.y - y });
  return {
    x,
    y,
    width,
    height,
    vector: vectorSchema.parse({
      width,
      height,
      closed,
      nodes: nodes.map((node) => ({
        ...shift(node),
        ...(node.in ? { in: shift(node.in) } : {}),
        ...(node.out ? { out: shift(node.out) } : {}),
      })),
    }),
  };
}

type TransformElement = {
  id: string;
  parentId: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
};
export type Matrix = [number, number, number, number, number, number];
const identity: Matrix = [1, 0, 0, 1, 0, 0];
export function multiply(a: Matrix, b: Matrix): Matrix {
  return [
    a[0] * b[0] + a[2] * b[1],
    a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3],
    a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4],
    a[1] * b[4] + a[3] * b[5] + a[5],
  ];
}
export function elementMatrix(
  element: TransformElement,
  elements: TransformElement[],
): Matrix {
  const chain = [element],
    seen = new Set([element.id]);
  let parentId = element.parentId;
  while (parentId) {
    if (seen.has(parentId)) throw new Error("Invalid parent cycle.");
    seen.add(parentId);
    const parent = elements.find((item) => item.id === parentId);
    if (!parent) throw new Error("Parent frame does not exist.");
    chain.unshift(parent);
    parentId = parent.parentId;
  }
  return chain.reduce((matrix, item) => {
    const r = (item.rotation * Math.PI) / 180,
      c = Math.cos(r),
      s = Math.sin(r);
    const cx = item.width / 2,
      cy = item.height / 2;
    return multiply(matrix, [
      c,
      s,
      -s,
      c,
      item.x + cx - c * cx + s * cy,
      item.y + cy - s * cx - c * cy,
    ]);
  }, identity);
}
export function transformPoint(matrix: Matrix, point: Point): Point {
  return {
    x: matrix[0] * point.x + matrix[2] * point.y + matrix[4],
    y: matrix[1] * point.x + matrix[3] * point.y + matrix[5],
  };
}
export function inversePoint(matrix: Matrix, point: Point): Point {
  const determinant = matrix[0] * matrix[3] - matrix[1] * matrix[2],
    x = point.x - matrix[4],
    y = point.y - matrix[5];
  return {
    x: (matrix[3] * x - matrix[2] * y) / determinant,
    y: (-matrix[1] * x + matrix[0] * y) / determinant,
  };
}
